import React from "react";
import Highcharts from "highcharts";
import { renderToString } from "react-dom/server";
import type { QUANTITY_COLOR_INDEX, SelectedQuantity } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";
import AxisTitle, { colorClassName } from "../AxisTitle";
import { createQuantityThresholdPlotLine } from "../series/createQuantityThresholdPlotLine";
import type { MeasuresChartType } from "../../Measures/MeasuresChartType";
import { groupBy } from "diagnostics-lib/iterable";
import type { Quantity } from "../../../../api/useQuantities";

const TICK_AMOUNT = 6;

function getAxisName(quantity: Quantity, axisGroupedByUnit: boolean) {
  return axisGroupedByUnit ? `quantity-unit-${quantity.unit}` : `quantity-id-${quantity.id}`;
}

export function isMultiAxis(selectedQuantities: SelectedQuantity[], axisGroupedByUnit: boolean) {
  return selectedQuantities.length === 1
    ? false
    : new Set(selectedQuantities.map(c => getAxisName(c.quantity, axisGroupedByUnit))).size > 1;
}

export function getAxisId(quantity: Quantity, axisGroupedByUnit: boolean, isMultiAxis: boolean) {
  // "axis-" prefix is needed so that axisIds do not clash with serieIds (triggers some Highcharts#18 somehow??)
  const id = getAxisName(quantity, axisGroupedByUnit);
  return `axis-${id}${isMultiAxis ? "" : "-unique"}`;
}

export function getAxisIds(selectedQuantities: SelectedQuantity[], axisGroupedByUnit: boolean) {
  const multiAxis = isMultiAxis(selectedQuantities, axisGroupedByUnit);
  return Array.from(
    new Set(selectedQuantities.map(c => getAxisId(c.quantity, axisGroupedByUnit, multiAxis))),
  ).sort();
}

function createPlotLines(
  quantity: SelectedQuantity["quantity"],
  selectedQuantity: SelectedQuantity,
  selectedChartOption?: "value" | "std",
) {
  const plotLineColor = quantityColor(selectedQuantity.colorIndex);
  const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];

  if (selectedChartOption === "std" && quantity.maxStdMetrology !== null)
    plotLines.push(createQuantityThresholdPlotLine(quantity, "maxStd", plotLineColor));
  // TODO: ADD ANALYSIS_FILTER PLOT BAND

  if (selectedChartOption === "value" && quantity.minMetrology !== null)
    plotLines.push(createQuantityThresholdPlotLine(quantity, "min", plotLineColor));
  // TODO: ADD ANALYSIS_FILTER PLOT BAND

  if (selectedChartOption === "value" && quantity.maxMetrology !== null)
    plotLines.push(createQuantityThresholdPlotLine(quantity, "max", plotLineColor));
  // TODO: ADD ANALYSIS_FILTER PLOT BAND

  return plotLines;
}

function useQuantityYAxes({
  selectedQuantities,
  axisGroupedByUnit,
  // multiColor = false,
  precision = false,
  threshold = false,
  selectedChartOption,
  axisTitle,
}: {
  selectedQuantities: SelectedQuantity[];
  axisGroupedByUnit: boolean;
  multiColor?: boolean;
  precision?: boolean;
  threshold?: boolean;
  selectedChartOption?: MeasuresChartType;
  axisTitle?: (name: string, color: QUANTITY_COLOR_INDEX | undefined) => React.ReactNode;
}): Highcharts.YAxisOptions[] {
  const multiAxis = isMultiAxis(selectedQuantities, axisGroupedByUnit);

  const selectedQuantitiesRef = React.useRef(selectedQuantities);
  selectedQuantitiesRef.current = selectedQuantities;

  const constantTickPositioner = React.useCallback(function (this: Highcharts.Axis) {
    if (
      this.tickPositions &&
      this.tickPositions.length < TICK_AMOUNT &&
      selectedQuantitiesRef.current.length > 1 // TODO do we need this?
    ) {
      return [
        ...this.tickPositions,
        Highcharts.correctFloat(
          (this.tickPositions[this.tickPositions.length - 1] as number) + this.tickInterval,
        ),
      ];
    }
    return this.tickPositions || [];
  }, []);

  return React.useMemo(() => {
    const selections = [
      ...groupBy(selectedQuantities, selection =>
        axisGroupedByUnit ? selection.quantity.unit : selection.quantity.id,
      ).entries(),
    ];

    const axis = selections.map(([_, quantities], i) => {
      const { id, text, quantity_unit, plotLines } = quantities.reduce<{
        id: string | null;
        text: string;
        quantity_unit: string | null;
        plotLines: Highcharts.YAxisPlotLinesOptions[];
      }>(
        (out, selectedQuantity) => {
          out.id = getAxisId(selectedQuantity.quantity, axisGroupedByUnit, multiAxis);
          out.quantity_unit = selectedQuantity.quantity.unit;

          out.text += renderToString(
            axisTitle === undefined ? (
              <AxisTitle className={colorClassName(selectedQuantity.colorIndex)}>
                {selectedQuantity.quantity.name}
              </AxisTitle>
            ) : (
              axisTitle(selectedQuantity.quantity.name, selectedQuantity.colorIndex)
            ),
          );

          if (threshold) {
            const plotLines = createPlotLines(
              selectedQuantity.quantity,
              selectedQuantity,
              selectedChartOption,
            );
            out.plotLines.push(...plotLines);
          }

          return out;
        },
        { id: null, text: "", plotLines: [], quantity_unit: null },
      );

      if (id === null) return null;

      return {
        id,
        tickAmount: multiAxis ? TICK_AMOUNT - 1 : undefined,
        startOnTick: multiAxis,
        endOnTick: multiAxis,
        title: {
          text: `${text} ${quantity_unit ? `(${quantity_unit})` : ""}`,
          textAlign: "center",
          useHTML: true,
        },
        labels: {
          unit: undefined,
          sign: precision,
          useHTML: true,
        },
        plotLines,
        opposite: i % 2 === 1,
        tickPositioner: constantTickPositioner,
        resize: {
          enabled: selectedQuantities.length < 2,
        },
        scrollbar: {
          enabled: !multiAxis,
        },
      };
    });

    return axis.filter(Boolean) as Highcharts.YAxisOptions[];
  }, [
    axisGroupedByUnit,
    selectedQuantities,
    constantTickPositioner,
    precision,
    multiAxis,
    threshold,
    selectedChartOption,
    axisTitle,
  ]);
}

export default useQuantityYAxes;
